import { useEffect } from 'react';

/**
 * Custom hook to encapsulate authentication, user info, and cleanup effects for Upload.jsx
 * @param {object} params - All required state and setters from Upload.jsx
 */
export default function useUploadAuthEffects({
  state,
  getAccessToken,
  setUserInfoError,
  setUserInfo,
  setIsFullyAuthenticated,
  isFullyAuthenticated,
  userInfoError,
  toast,
  clearPolling,
}) {
  // Fetch user information from Asgardeo userinfo endpoint when authenticated
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (state.isAuthenticated) {
        try {
          // Get the access token
          const accessToken = await getAccessToken();

          setUserInfoError(false); // Reset error state on new attempt

          // Determine the organization name from the baseUrl in asgardeoConfig
          // Using fallback to prevent errors if we can't parse it
          let orgName = 'paralegallk'; // Fallback organization name
          try {
            const baseUrlEnv = process.env.REACT_APP_ASGARDEO_BASE_URL;
            if (baseUrlEnv) {
              const matches = baseUrlEnv.match(/\/t\/([^/]+)\//);
              if (matches && matches[1]) {
                orgName = matches[1];
              }
            }
          } catch (error) {
            console.error('Error extracting organization name:', error);
          }

          // Call the userinfo endpoint
          const response = await fetch(
            `https://api.asgardeo.io/t/${orgName}/oauth2/userinfo`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const userInfoData = await response.json();
          console.log('Retrieved user info:', userInfoData);

          // Store user information in state and session storage
          setUserInfo(userInfoData);
          // Note: email and userId are now managed by CreditProvider context

          // Store in session storage with expiry
          const tokenExpiryTime = Date.now() + 3600 * 1000; // 1 hour from now
          const userData = {
            ...userInfoData,
            authToken: accessToken,
            tokenExpiry: tokenExpiryTime,
          };

          sessionStorage.setItem('user', JSON.stringify(userData));
          console.log('User information stored in session storage');

          // Successfully retrieved user info, but don't set fully authenticated yet
          // Let the separate useEffect handle that after a delay
          console.log('User info retrieved successfully');
        } catch (error) {
          console.error('Failed to fetch user info:', error);
          // toast.error('Failed to retrieve user information');
          setUserInfoError(true); // Set error flag when fetch fails
        }
      }
    };

    fetchUserInfo();
  }, [state.isAuthenticated, getAccessToken]);

  // Only handle authentication status changes here
  useEffect(() => {
    if (state.isAuthenticated && !isFullyAuthenticated && !userInfoError) {
      // Only proceed if there was no error fetching user info
      const timer = setTimeout(() => {
        setIsFullyAuthenticated(true);
        console.log('User is now fully authenticated');
      }, 500); // 0.5 second delay after authentication to ensure tokens are processed

      return () => clearTimeout(timer);
    }
  }, [state.isAuthenticated, userInfoError]);

  // Separate effect for toast notifications to avoid race conditions
  useEffect(() => {
    if (isFullyAuthenticated && !userInfoError) {
      toast.success('Authentication complete! You can now upload documents.');
    }
  }, [isFullyAuthenticated, userInfoError]);

  // Reset full authentication status when auth state changes
  useEffect(() => {
    if (!state.isAuthenticated) {
      setIsFullyAuthenticated(false);
    }
  }, [state.isAuthenticated]);

  // Enhanced cleanup on unmount
  useEffect(() => {
    return () => {
      console.log(' Component unmounting, cleaning up...');
      clearPolling();
    };
  }, [clearPolling]);
} 